import { useState, FormEvent, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthCard, PasswordInput, VerificationCodeInput } from '@/components/auth';
import { useAuthFlow } from '@/hooks/useAuthFlow';
import { usePasswordValidation } from '@/hooks/usePasswordValidation';
import { Loader2, AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const { resetPassword, isSubmitting, error, clearError, pendingEmail } = useAuthFlow();

  // Get email from pending email (wouter doesn't support state in navigation)
  const email = pendingEmail || '';

  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    code?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const passwordValidation = usePasswordValidation(newPassword);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      setLocation('/forgot-password');
    }
  }, [email, setLocation]);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!code || code.length !== 6) {
      errors.code = 'Please enter the 6-digit code';
    }

    if (!newPassword) {
      errors.password = 'New password is required';
    } else if (!passwordValidation.isValid) {
      errors.password = 'Password does not meet requirements';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) {
      return;
    }

    const result = await resetPassword(email, code, newPassword);

    if (result.success) {
      setResetSuccess(true);
      toast.success('Password reset successfully!');

      // Short delay to show success, then redirect to login
      setTimeout(() => {
        setLocation('/login');
      }, 2000);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    clearError();
    try {
      // Use forgotPassword to resend the code
      const { requestPasswordReset } = await import('@/services/auth');
      const result = await requestPasswordReset(email);

      if (result.success) {
        toast.success('Reset code sent!');
        setResendCooldown(60);
        setCode('');
      } else {
        toast.error(result.error?.message || 'Failed to send reset code');
      }
    } catch {
      toast.error('Failed to send reset code. Please try again.');
    }
  };

  if (resetSuccess) {
    return (
      <AuthCard
        title="Password Reset!"
        description="Your password has been changed successfully"
      >
        <div className="flex flex-col items-center py-6">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <p className="text-muted-foreground text-center">
            Redirecting to login...
          </p>
          <Loader2 className="h-5 w-5 animate-spin mt-4 text-amber-500" />
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Set new password"
      description="Enter the code we sent and your new password"
      footer={
        <p>
          Remember your password?{' '}
          <Link href="/login" className="text-amber-600 hover:text-amber-700 font-medium">
            Sign in
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email indicator */}
        <div className="flex items-center justify-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
          <Mail className="h-5 w-5 text-amber-600" />
          <span className="text-sm font-medium">{email}</span>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <VerificationCodeInput
          value={code}
          onChange={(value) => {
            setCode(value);
            if (validationErrors.code) {
              setValidationErrors(prev => ({ ...prev, code: undefined }));
            }
          }}
          disabled={isSubmitting}
          label="Verification Code"
          error={validationErrors.code}
        />

        <PasswordInput
          id="newPassword"
          label="New Password"
          value={newPassword}
          onChange={(value) => {
            setNewPassword(value);
            if (validationErrors.password) {
              setValidationErrors(prev => ({ ...prev, password: undefined }));
            }
          }}
          placeholder="Create a strong password"
          showStrength={true}
          error={validationErrors.password}
          disabled={isSubmitting}
        />

        <PasswordInput
          id="confirmPassword"
          label="Confirm New Password"
          value={confirmPassword}
          onChange={(value) => {
            setConfirmPassword(value);
            if (validationErrors.confirmPassword) {
              setValidationErrors(prev => ({ ...prev, confirmPassword: undefined }));
            }
          }}
          placeholder="Confirm your password"
          error={validationErrors.confirmPassword}
          disabled={isSubmitting}
        />

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Resetting password...
            </>
          ) : (
            'Reset Password'
          )}
        </Button>

        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Didn&apos;t receive the code?
          </p>
          <Button
            type="button"
            variant="ghost"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isSubmitting}
            className="text-amber-600 hover:text-amber-700"
          >
            {resendCooldown > 0 ? (
              `Resend code in ${resendCooldown}s`
            ) : (
              'Resend reset code'
            )}
          </Button>
        </div>
      </form>
    </AuthCard>
  );
}
